/**
 * SearchBar.js - Enhanced search input WITH Netflix-style dropdown suggestions
 *
 * Changes:
 * - Restored dropdown suggestions for Netflix-style experience
 * - Suggestions appear as you type with smooth animations
 * - Keyboard navigation support (up/down arrows, enter, escape)
 * - Click outside to close functionality
 */
import React, { memo, useState, useEffect, useRef } from 'react';
import { useSearch } from '../../hooks/useSearch';

/**
 * SearchBar component with Netflix-style dropdown suggestions
 * @param {Object} props - Component props
 * @returns {JSX.Element} SearchBar component
 */
const SearchBar = ({
  searchState,
  className = '',
  placeholder = 'Search for movies, shows, and more...',
  size = 'default', // 'small', 'default', 'large'
}) => {
  const {
    query,
    textSuggestions,
    suggestionsLoading,
    isRealTimeSearch,
    handleSearchChange,
    handleSearchSubmit,
    clearSearch,
    selectSuggestion,
    searchInputRef,
  } = searchState;

  // Local state for dropdown
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  // Get size classes
  const sizeClasses = {
    small: 'px-3 py-2 text-sm',
    default: 'px-4 py-3',
    large: 'px-5 py-4 text-lg',
  };

  // Show dropdown when there are suggestions and query length >= 2
  useEffect(() => {
    const shouldShow = query.length >= 2 && textSuggestions.length > 0;
    setShowDropdown(shouldShow);
    setSelectedIndex(-1); // Reset selection when suggestions change
  }, [query, textSuggestions]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || textSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < textSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < textSuggestions.length) {
          selectSuggestion(textSuggestions[selectedIndex]);
          setShowDropdown(false);
        } else {
          handleSearchSubmit(e);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        searchInputRef.current?.blur();
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    selectSuggestion(suggestion);
    setShowDropdown(false);
    setSelectedIndex(-1);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (query.length >= 2 && textSuggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative w-full max-w-md mx-auto ${className}`} ref={dropdownRef}>
      <form onSubmit={handleSearchSubmit} className="w-full">
        <div className="relative flex items-center">
          {/* Search Icon */}
          <div className="absolute z-10 left-3">
            {suggestionsLoading ? (
              <div className="w-5 h-5 border-2 rounded-full border-white/20 border-t-white/40 animate-spin" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 transition-colors duration-200 text-white/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            )}
          </div>

          {/* Search Input */}
          <input
            ref={searchInputRef}
            type="text"
            className={`w-full pl-10 pr-12 rounded-full border transition-all duration-200 text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-black/40 focus:shadow-lg focus:shadow-white/10 border-white/20 bg-black/20 ${
              sizeClasses[size]
            } ${isRealTimeSearch ? 'bg-black/30' : ''}`}
            placeholder={placeholder}
            value={query}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            aria-label="Search input"
            autoComplete="off"
            spellCheck="false"
            aria-expanded={false}
            aria-haspopup="false"
            role="searchbox"
          />

          {/* Real-time Search Indicator */}
          {isRealTimeSearch && (
            <div className="absolute -translate-y-1/2 right-12 top-1/2">
              <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" />
            </div>
          )}

          {/* Clear Button */}
          {query && (
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                clearSearch();
                setShowDropdown(false);
                setSelectedIndex(-1);
              }}
              className="absolute p-1 transition-colors duration-200 -translate-y-1/2 right-3 top-1/2 text-white/50 hover:text-white focus:outline-none focus:text-teal-500"
              aria-label="Clear search"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </form>

      {/* Netflix-style Dropdown Suggestions - DISABLED per user request */}
      {/* Keep suggestions data for horizontal display but don't show dropdown */}
      {false && showDropdown && (
        <div className="absolute top-full left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border border-white/20 border-t-0 rounded-b-lg shadow-2xl max-h-80 overflow-y-auto">
          <div className="py-2">
            {textSuggestions.slice(0, 8).map((suggestion, index) => (
              <button
                key={`dropdown-suggestion-${index}`}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full px-4 py-3 text-left transition-all duration-150 flex items-center gap-3 hover:bg-white/10 focus:outline-none focus:bg-white/10 ${
                  selectedIndex === index ? 'bg-white/10 text-teal-300' : 'text-white/80'
                }`}
                role="option"
                aria-selected={selectedIndex === index}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 text-white/40"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <span className="truncate">{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

SearchBar.displayName = 'SearchBar';

export default memo(SearchBar);

